import { useRef, useCallback } from "react";

// Ringtone concern for WebRTC calls — self-contained WebAudio, decoupled from the
// peer-connection / call-state logic in CallContext. Owns its own audio-context ref.
export const RINGTONES = [
  { id: "howl", name: "Howl", type: "sine", notes: [392, 523, 659], step: 0.18, loop: 2200 },
  { id: "classic", name: "Classic", type: "square", notes: [880, 0, 880], step: 0.2, loop: 1600 },
  { id: "chime", name: "Chime", type: "triangle", notes: [659, 784, 988], step: 0.16, loop: 2400 },
  { id: "pulse", name: "Pulse", type: "sine", notes: [520, 520], step: 0.14, loop: 1200 },
  { id: "cosmic", name: "Cosmic", type: "triangle", notes: [330, 494, 740, 988], step: 0.13, loop: 2600 },
];

export const getSavedRingtone = () => (typeof localStorage !== "undefined" && localStorage.getItem("ps_ringtone")) || "howl";

const playPattern = (ctx, rt) => {
  rt.notes.forEach((f, i) => {
    if (!f) return;
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = rt.type; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
    const t0 = ctx.currentTime + i * rt.step;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + rt.step * 0.9);
    o.start(t0); o.stop(t0 + rt.step + 0.02);
  });
};

export const previewRingtone = (id) => {
  const rt = RINGTONES.find((r) => r.id === id) || RINGTONES[0];
  try { const Ctx = window.AudioContext || window.webkitAudioContext; const ctx = new Ctx(); playPattern(ctx, rt); setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1500); } catch (e) { /* audio not available */ }
};

// Returns stable { startRing, stopRing }. startRing(true) => incoming ringtone pattern,
// startRing(false) => outgoing dial beep. stopRing tears down the audio context.
export function useRingtone() {
  const ringRef = useRef(null);

  const startRing = useCallback((incoming) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ringRef.current = { ctx, timer: null };
      if (incoming) {
        const rt = RINGTONES.find((r) => r.id === getSavedRingtone()) || RINGTONES[0];
        playPattern(ctx, rt);
        ringRef.current.timer = setInterval(() => playPattern(ctx, rt), rt.loop);
      } else {
        const beep = () => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.frequency.value = 440; o.type = "sine"; o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(0.0001, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.05);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
          o.start(); o.stop(ctx.currentTime + 0.55);
        };
        beep();
        ringRef.current.timer = setInterval(beep, 3000);
      }
    } catch (e) { /* audio not available */ }
  }, []);

  const stopRing = useCallback(() => {
    const r = ringRef.current;
    if (r) { clearInterval(r.timer); try { r.ctx.close(); } catch (e) {} ringRef.current = null; }
  }, []);

  return { startRing, stopRing };
}
